import { useCallback, useEffect, useRef, useState } from 'react';
import { loadStore, newDraft, saveStore, type Draft, type DraftStore } from '../lib/storage';

/**
 * Manages multiple drafts in localStorage with debounced auto-save.
 */
export function useDrafts() {
  const [store, setStore] = useState<DraftStore>(() => loadStore());
  const saveTimer = useRef<number | null>(null);

  // Debounced save on change
  useEffect(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => saveStore(store), 250);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [store]);

  // Save immediately on tab close
  useEffect(() => {
    const handler = () => saveStore(store);
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [store]);

  const current = store.drafts[store.currentId];

  const updateCurrent = useCallback((patch: Partial<Draft>) => {
    setStore(prev => {
      const cur = prev.drafts[prev.currentId];
      if (!cur) return prev;
      return {
        ...prev,
        drafts: {
          ...prev.drafts,
          [cur.id]: { ...cur, ...patch, lastModified: Date.now() },
        },
      };
    });
  }, []);

  const switchDraft = useCallback((id: string) => {
    setStore(prev => prev.drafts[id] ? { ...prev, currentId: id } : prev);
  }, []);

  const createDraft = useCallback((name?: string) => {
    const d = newDraft(name);
    setStore(prev => ({
      currentId: d.id,
      drafts: { ...prev.drafts, [d.id]: d },
    }));
    return d.id;
  }, []);

  const deleteDraft = useCallback((id: string) => {
    setStore(prev => {
      const { [id]: _gone, ...rest } = prev.drafts;
      void _gone;
      if (Object.keys(rest).length === 0) {
        const d = newDraft();
        return { currentId: d.id, drafts: { [d.id]: d } };
      }
      const nextCurrent = prev.currentId === id ? Object.keys(rest)[0] : prev.currentId;
      return { currentId: nextCurrent, drafts: rest };
    });
  }, []);

  const renameDraft = useCallback((id: string, name: string) => {
    setStore(prev => {
      const d = prev.drafts[id];
      if (!d) return prev;
      return {
        ...prev,
        drafts: { ...prev.drafts, [id]: { ...d, name, lastModified: Date.now() } },
      };
    });
  }, []);

  return {
    current,
    drafts: Object.values(store.drafts).sort((a, b) => b.lastModified - a.lastModified),
    currentId: store.currentId,
    updateCurrent,
    switchDraft,
    createDraft,
    deleteDraft,
    renameDraft,
  };
}
